import React, { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { FaInfoCircle, FaTimes } from "react-icons/fa";
import { functions } from "../firebase";
import { useToast } from "./Toast";
import useIsDemo from "../hooks/useIsDemo";
import { colors, spacing, typography, radius } from "../utils/designTokens";

/**
 * Banner persistente que aparece SOLO en modo demo. Avisa al visitante que
 * esta en una demo de solo lectura y le ofrece un CTA para contactar a un
 * asesor (sin esperar al limite de Habi).
 *
 * Se renderiza al tope del shell de la app (App.js MainApp) cuando `useIsDemo`
 * devuelve true. En login normal NO aparece.
 */
const DemoBanner = () => {
  const { isDemo, loading } = useIsDemo();
  const { showToast } = useToast();
  const [dismissed, setDismissed] = useState(false);
  const [contacting, setContacting] = useState(false);

  if (loading || !isDemo || dismissed) return null;

  const handleContact = async () => {
    if (contacting) return;
    setContacting(true);
    try {
      const fn = httpsCallable(functions, "requestDemoContact");
      await fn({ preferredContactTime: "", comments: "Desde el banner" });
      showToast("Listo, un asesor te contacta en breve.", "success");
    } catch (e) {
      console.error("requestDemoContact error:", e);
      showToast(e.message || "No pudimos registrar tu pedido.", "error");
    } finally {
      setContacting(false);
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.info} 100%)`,
        color: colors.white,
        padding: `${spacing[2]}px ${spacing[4]}px`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing[3],
        fontSize: typography.sizes.sm,
        fontWeight: 400,
        flexWrap: "wrap",
      }}
    >
      <FaInfoCircle size={14} />
      <span>
        Estás viendo el <b style={{ fontWeight: 400 }}>demo de Habitia</b> en modo solo lectura.
      </span>
      <button
        onClick={handleContact}
        disabled={contacting}
        style={{
          background: colors.white,
          color: colors.primary,
          border: "none",
          borderRadius: radius.full,
          padding: "4px 12px",
          fontSize: typography.sizes.sm,
          fontWeight: 400,
          fontFamily: "inherit",
          cursor: contacting ? "wait" : "pointer",
          opacity: contacting ? 0.6 : 1,
        }}
      >
        {contacting ? "Enviando..." : "Quiero contratar Habitia"}
      </button>
      <button
        onClick={() => setDismissed(true)}
        aria-label="Cerrar"
        style={{
          background: "transparent",
          color: colors.white,
          border: "none",
          cursor: "pointer",
          marginLeft: 4,
          opacity: 0.7,
        }}
      >
        <FaTimes size={12} />
      </button>
    </div>
  );
};

export default DemoBanner;
